import {Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {OntologyListEntity, RelatableSelectableItem, ScenarioForDisplayability} from '../../FilterBannerModels';
import {isItemAbleToShow} from '../../UpdatedFilterBannerHelpers';

@Component({
  selector: 'app-search-specialty',
  templateUrl: './search-specialty.component.html',
  styleUrls: ['./search-specialty.component.scss']
})
export class SearchSpecialtyComponent implements OnChanges, OnInit {

  @Input() specialties: RelatableSelectableItem<OntologyListEntity>[] = [];
  @Input() whenToggled: (open: boolean) => void;
  @Input() showDepartments = false;
  @Output() afterSpecialtiesUpdated = new EventEmitter<boolean>();
  selectedSpecialties: RelatableSelectableItem<OntologyListEntity>[] = [];
  unselectedSpecialties: RelatableSelectableItem<OntologyListEntity>[] = [];
  displayedSelectedSpecialties: RelatableSelectableItem<OntologyListEntity>[] = [];
  displayedUnselectedSpecialties: RelatableSelectableItem<OntologyListEntity>[] = [];
  hiddenSelectedSpecialties: RelatableSelectableItem<OntologyListEntity>[] = [];
  hiddenUnselectedSpecialties: RelatableSelectableItem<OntologyListEntity>[] = [];
  showSpecialties = false;
  readonly DEFAULT_SPECIALTIES = 'All Specialties';
  toggleLabel = this.DEFAULT_SPECIALTIES;
  additionalLabel = '';
  toggleInfo: BehaviorSubject<boolean>;
  searchText = '';

  constructor() {
    this.toggleInfo = new BehaviorSubject<boolean>(false);
    this.getToggleInfo().subscribe((showSpecialties: boolean) => {
      this.showSpecialties = showSpecialties;
      if (this.whenToggled) {
        this.whenToggled(showSpecialties);
      }
    });
  }

  ngOnInit(): void {
    this.filterSpecialtyLists();
    this.setToggleLabel();
  }

  ngOnChanges() {
    if (this.showDepartments) {
      this.toggleInfo.next(false);
    }
  }

  private filterSpecialtyLists(): void {
    this.selectedSpecialties = this.specialties.filter(s => s.item.selected && s.item.belongs);
    this.sortSpecialties(this.selectedSpecialties);
    this.unselectedSpecialties = this.specialties.filter(s => !s.item.selected && s.item.belongs);
    this.sortSpecialties(this.unselectedSpecialties);
  }

  private sortSpecialties(specialtyNodes: RelatableSelectableItem<OntologyListEntity>[]): void {
    const defaultSortResult = 0;
    specialtyNodes.sort((specialtyNode1, specialtyNode2) => {
      return (specialtyNode1.item?.item?.specialtyNodeName && specialtyNode2.item?.item?.specialtyNodeName) ?
        (specialtyNode1.item.item.specialtyNodeName.localeCompare(specialtyNode2.item.item.specialtyNodeName)) : defaultSortResult;
    });
  }

  private filterDisplayedSpecialties(): void {
    const allSelectedDisplayedSpecialties = this.displayedSelectedSpecialties.concat(this.hiddenSelectedSpecialties);
    this.displayedSelectedSpecialties = allSelectedDisplayedSpecialties.filter(s =>
      isItemAbleToShow(s, ScenarioForDisplayability.Standard));
    this.hiddenSelectedSpecialties = allSelectedDisplayedSpecialties.filter(s => !isItemAbleToShow(s,
      ScenarioForDisplayability.Standard));
    const allUnselectedDisplayedSpecialties = this.displayedUnselectedSpecialties.concat(this.hiddenUnselectedSpecialties);
    this.displayedUnselectedSpecialties = allUnselectedDisplayedSpecialties.filter(s =>
      isItemAbleToShow(s, ScenarioForDisplayability.Standard));
    this.hiddenUnselectedSpecialties = allUnselectedDisplayedSpecialties.filter(s => !isItemAbleToShow(s,
      ScenarioForDisplayability.Standard));
    this.sortSpecialties(this.displayedSelectedSpecialties);
    this.sortSpecialties(this.displayedUnselectedSpecialties);
  }

  getToggleInfo(): Observable<boolean> {
    return this.toggleInfo.asObservable();
  }

  toggleSpecialties(): void {
    if (!this.toggleInfo.value) {
      this.displayedSelectedSpecialties = this.selectedSpecialties.slice();
      this.displayedUnselectedSpecialties = this.unselectedSpecialties.slice();
    }
    this.toggleInfo.next(!this.toggleInfo.value);
  }

  onSearchTextChanged(): void {
    this.specialties.forEach(specialty => {
      specialty.item.matchesSearchText = this.searchText.length < 3 ||
        !!specialty.item.item.specialtyNodeName?.toLowerCase().includes(this.searchText.toLowerCase());
    });
    this.filterDisplayedSpecialties();
  }

  selectAllSpecialties(event: MouseEvent): void {
    event.preventDefault();
    if (this.displayedSelectedSpecialties.length && !this.displayedUnselectedSpecialties.length) {
      this.displayedSelectedSpecialties.forEach(specialty => {
        specialty.item.selected = false;
      });
      this.displayedUnselectedSpecialties = this.displayedUnselectedSpecialties.concat(this.displayedSelectedSpecialties);
      this.displayedSelectedSpecialties = [];
    } else {
      this.displayedUnselectedSpecialties.forEach(specialty => {
        specialty.item.selected = true;
      });
      this.displayedSelectedSpecialties = this.displayedSelectedSpecialties.concat(this.displayedUnselectedSpecialties);
      this.displayedUnselectedSpecialties = [];
    }
  }

  whenSpecialtyClicked(specialty: RelatableSelectableItem<OntologyListEntity>, event: MouseEvent): void {
    event.preventDefault();
    specialty.item.selected = !specialty.item.selected;
    if (specialty.item.selected) {
      this.displayedUnselectedSpecialties = this.displayedUnselectedSpecialties.filter(s => s !== specialty);
      this.displayedSelectedSpecialties.push(specialty);
      this.sortSpecialties(this.displayedSelectedSpecialties);
    } else {
      this.displayedSelectedSpecialties = this.displayedSelectedSpecialties.filter(s => s !== specialty);
      this.displayedUnselectedSpecialties.push(specialty);
      this.sortSpecialties(this.displayedUnselectedSpecialties);
    }
  }

  private setToggleLabel(): void {
    if (this.selectedSpecialties.length === 0 || this.unselectedSpecialties.length === 0) {
      this.toggleLabel = this.DEFAULT_SPECIALTIES;
    } else {
      this.toggleLabel = this.selectedSpecialties[0].item.item.specialtyNodeName || this.toggleLabel;
      this.additionalLabel = '{+ ' + (this.selectedSpecialties.length - 1) + ' More}';
    }
  }

  cancel(): void {
    this.toggleInfo.next(false);
  }

  apply(): void {
    this.toggleInfo.next(false);
    this.selectedSpecialties = this.displayedSelectedSpecialties.slice();
    this.unselectedSpecialties = this.displayedUnselectedSpecialties.slice();
    this.filterSpecialtyLists();
    this.afterSpecialtiesUpdated.emit(!!this.selectedSpecialties.length);
    this.setToggleLabel();
  }
}
